/**
 * Integrations API module — Jira and Linear OAuth + export.
 */
import type {
  Integration,
  JiraProject,
  JiraIssueType,
  LinearTeam,
  LinearLabel,
  ExportResult,
  SlackChannel,
  AsanaWorkspace,
  AsanaProject,
  ClickUpWorkspace, ClickUpSpace, ClickUpList,
  TrelloBoard,
  TrelloList,
} from '@overlayqa/shared';
import { runChunkedExport } from '@overlayqa/shared';
import { fetchApi } from './client';

import { log } from '@/lib/logger';
import { MUTATION_TIMEOUT_MS, SCREEN_READ_TIMEOUT_MS } from './requestTimeout';

/**
 * POST an export in chunks the route accepts. Every export route caps
 * `ticketIds` at EXPORT_MAX_IDS_PER_REQUEST, and until 2026-09-09 this client
 * sent every selected id in one request, so a 51-issue selection 400'd with
 * "Invalid request. Please check your input and try again." Chunking here
 * covers every caller (bulk toolbar, row kebab, ticket detail) at once. A
 * selection at or under the cap is one request whose body is byte-identical
 * to the old one: `{ ...data, ticketIds }` rewrites the existing key in place.
 */
function exportInChunks<TData extends { ticketIds: string[] }>(path: string, token: string | undefined, data: TData) {
  return runChunkedExport(data.ticketIds, (ticketIds) =>
    fetchApi<{ results: ExportResult[] }>(path, {
      token,
      method: 'POST',
      body: JSON.stringify({ ...data, ticketIds }),
    }),
  );
}

log.info('[api/integrations] Module loaded');

function slackPath(path: string, teamId?: string): string {
  return `/integrations/slack${path}${teamId ? `?teamId=${encodeURIComponent(teamId)}` : ''}`;
}

export const integrationsApi = {
  list: (token: string | undefined) =>
    fetchApi<Integration[]>('/integrations', { token, timeoutMs: SCREEN_READ_TIMEOUT_MS }),

  // Jira
  getJiraAuthUrl: (token: string | undefined) =>
    fetchApi<{ url: string; state: string }>('/integrations/jira/auth', {
      token,
      method: 'POST',
      timeoutMs: MUTATION_TIMEOUT_MS,
    }),

  disconnectJira: (token: string | undefined) =>
    fetchApi<{ success: boolean }>('/integrations/jira', {
      token,
      method: 'DELETE',
      timeoutMs: MUTATION_TIMEOUT_MS,
    }),

  getJiraProjects: (token: string | undefined) =>
    fetchApi<{
      resources: Array<{ id: string; name: string; url: string }>;
      projects: JiraProject[];
    }>('/integrations/jira/projects', { token }),

  getJiraIssueTypes: (token: string | undefined, cloudId: string, projectKey: string) =>
    fetchApi<JiraIssueType[]>(
      `/integrations/jira/issue-types?cloudId=${cloudId}&projectKey=${projectKey}`,
      { token }
    ),

  exportToJira: (
    token: string | undefined,
    data: {
      ticketIds: string[];
      cloudId: string;
      projectKey: string;
      issueTypeId: string;
      siteUrl?: string;
    }
  ) =>
    exportInChunks('/integrations/jira/export', token, data),

  // Linear
  getLinearAuthUrl: (token: string | undefined) =>
    fetchApi<{ url: string; state: string }>('/integrations/linear/auth', {
      token,
      method: 'POST',
      timeoutMs: MUTATION_TIMEOUT_MS,
    }),

  disconnectLinear: (token: string | undefined) =>
    fetchApi<{ success: boolean }>('/integrations/linear', {
      token,
      method: 'DELETE',
      timeoutMs: MUTATION_TIMEOUT_MS,
    }),

  getLinearTeams: (token: string | undefined) =>
    fetchApi<LinearTeam[]>('/integrations/linear/teams', { token }),

  getLinearLabels: (token: string | undefined, teamId: string) =>
    fetchApi<LinearLabel[]>(`/integrations/linear/labels?teamId=${teamId}`, { token }),

  exportToLinear: (
    token: string | undefined,
    data: {
      ticketIds: string[];
      teamId: string;
      labelIds?: string[];
    }
  ) =>
    exportInChunks('/integrations/linear/export', token, data),

  // Notion
  getNotionAuthUrl: (token: string | undefined) =>
    fetchApi<{ url: string; state: string }>('/integrations/notion/auth', {
      token,
      method: 'POST',
      timeoutMs: MUTATION_TIMEOUT_MS,
    }),

  disconnectNotion: (token: string | undefined) =>
    fetchApi<{ success: boolean }>('/integrations/notion', {
      token,
      method: 'DELETE',
      timeoutMs: MUTATION_TIMEOUT_MS,
    }),

  exportToNotion: (
    token: string | undefined,
    data: { ticketIds: string[] }
  ) =>
    exportInChunks('/integrations/notion/export', token, data),

  // Asana
  getAsanaAuthUrl: (token: string | undefined) =>
    fetchApi<{ url: string; state: string }>('/integrations/asana/auth', {
      token,
      method: 'POST',
      timeoutMs: MUTATION_TIMEOUT_MS,
    }),

  disconnectAsana: (token: string | undefined) =>
    fetchApi<{ success: boolean }>('/integrations/asana', {
      token,
      method: 'DELETE',
      timeoutMs: MUTATION_TIMEOUT_MS,
    }),

  getAsanaWorkspaces: (token: string | undefined) =>
    fetchApi<AsanaWorkspace[]>('/integrations/asana/workspaces', { token }),

  getAsanaProjects: (token: string | undefined, workspaceGid: string) =>
    fetchApi<AsanaProject[]>(
      `/integrations/asana/projects?workspaceGid=${workspaceGid}`,
      { token }
    ),

  exportToAsana: (
    token: string | undefined,
    data: {
      ticketIds: string[];
      projectGid: string;
    }
  ) =>
    exportInChunks('/integrations/asana/export', token, data),

  getClickUpWorkspaces: (token: string | undefined) => fetchApi<ClickUpWorkspace[]>('/integrations/clickup/workspaces', { token }),
  getClickUpSpaces: (token: string | undefined, workspaceId: string) => fetchApi<ClickUpSpace[]>(`/integrations/clickup/spaces?${new URLSearchParams({ workspaceId })}`, { token }),
  getClickUpLists: (token: string | undefined, spaceId: string) => fetchApi<ClickUpList[]>(`/integrations/clickup/lists?${new URLSearchParams({ spaceId })}`, { token }),
  exportToClickUp: (token: string | undefined, data: { ticketIds: string[]; workspaceId: string; spaceId: string; listId: string }) => exportInChunks('/integrations/clickup/export', token, data),

  getGitHubAuthUrl: (token: string | undefined) => fetchApi<{ url: string; state: string }>('/integrations/github/auth', { token, method: 'POST', timeoutMs: MUTATION_TIMEOUT_MS }),
  disconnectGitHub: (token: string | undefined) => fetchApi<{ success: boolean }>('/integrations/github', { token, method: 'DELETE', timeoutMs: MUTATION_TIMEOUT_MS }),
  getClickUpAuthUrl: (token: string | undefined) => fetchApi<{ url: string; state: string }>('/integrations/clickup/auth', { token, method: 'POST', timeoutMs: MUTATION_TIMEOUT_MS }),
  disconnectClickUp: (token: string | undefined) => fetchApi<{ success: boolean }>('/integrations/clickup', { token, method: 'DELETE', timeoutMs: MUTATION_TIMEOUT_MS }),

  // Basecamp's project and to-do list reads and its export go through the
  // shared export dialog (createExportApi in lib/exportDialog.tsx), so only
  // the connect and disconnect calls live here.
  getBasecampAuthUrl: (token: string | undefined) => fetchApi<{ url: string; state: string }>('/integrations/basecamp/auth', { token, method: 'POST', timeoutMs: MUTATION_TIMEOUT_MS }),
  disconnectBasecamp: (token: string | undefined) => fetchApi<{ success: boolean }>('/integrations/basecamp', { token, method: 'DELETE', timeoutMs: MUTATION_TIMEOUT_MS }),
  // monday.com: boards, groups and export also go through the shared dialog.
  getMondayAuthUrl: (token: string | undefined) => fetchApi<{ url: string; state: string }>('/integrations/monday/auth', { token, method: 'POST', timeoutMs: MUTATION_TIMEOUT_MS }),
  disconnectMonday: (token: string | undefined) => fetchApi<{ success: boolean }>('/integrations/monday', { token, method: 'DELETE', timeoutMs: MUTATION_TIMEOUT_MS }),

  // Trello
  getTrelloAuthUrl: (token: string | undefined) =>
    fetchApi<{ url: string; state: string }>('/integrations/trello/auth', {
      token,
      method: 'POST',
      timeoutMs: MUTATION_TIMEOUT_MS,
    }),

  disconnectTrello: (token: string | undefined) =>
    fetchApi<{ success: boolean }>('/integrations/trello', {
      token,
      method: 'DELETE',
      timeoutMs: MUTATION_TIMEOUT_MS,
    }),

  getTrelloBoards: (token: string | undefined) =>
    fetchApi<TrelloBoard[]>('/integrations/trello/boards', { token }),

  getTrelloLists: (token: string | undefined, boardId: string) =>
    fetchApi<TrelloList[]>(`/integrations/trello/boards/${boardId}/lists`, { token }),

  exportToTrello: (
    token: string | undefined,
    data: {
      ticketIds: string[];
      boardId: string;
      listId: string;
      metadata?: Record<string, string | string[] | null>;
    }
  ) =>
    exportInChunks('/integrations/trello/export', token, data),

  // Slack
  getSlackAuthUrl: (token: string | undefined, teamId?: string) =>
    fetchApi<{ url: string; state: string }>(slackPath('/auth', teamId), {
      token,
      method: 'POST',
      timeoutMs: MUTATION_TIMEOUT_MS,
    }),

  disconnectSlack: (token: string | undefined, teamId?: string) =>
    fetchApi<{ success: boolean }>(slackPath('', teamId), {
      token,
      method: 'DELETE',
      timeoutMs: MUTATION_TIMEOUT_MS,
    }),

  getSlackStatus: (token: string | undefined, teamId?: string) =>
    fetchApi<{
      connected: boolean;
      slackTeamName?: string;
      channelId?: string | null;
      channelName?: string | null;
      isActive?: boolean;
      createdAt?: string;
    }>(slackPath('/status', teamId), { token, timeoutMs: SCREEN_READ_TIMEOUT_MS }),

  getSlackChannels: (token: string | undefined, teamId?: string) =>
    fetchApi<{ channels: SlackChannel[]; selectedChannelId: string | null }>(
      slackPath('/channels', teamId),
      { token }
    ),

  setSlackChannel: (token: string | undefined, channelId: string, channelName: string, teamId?: string) =>
    fetchApi<{ success: boolean }>(slackPath('/channel', teamId), {
      token,
      method: 'POST',
      timeoutMs: MUTATION_TIMEOUT_MS,
      body: JSON.stringify({ channelId, channelName }),
    }),

  testSlackConnection: (token: string | undefined, teamId?: string) =>
    fetchApi<{ success: boolean }>(slackPath('/test', teamId), {
      token,
      method: 'POST',
      timeoutMs: MUTATION_TIMEOUT_MS,
    }),
};
